import React from 'react';

/**
 * Temperature gauge component
 * @param {Object} props Component properties
 * @param {number|null} props.temperature Current temperature value or null if not available
 * @param {number} props.targetTemp Target temperature value
 * @param {number} [props.min=28] Minimum temperature for gauge scale
 * @param {number} [props.max=100] Maximum temperature for gauge scale
 */
const TemperatureGauge = ({ temperature, targetTemp, min = 28, max = 100 }) => {
  // Handle null values
  const currentTemp = temperature !== null ? temperature : '--';
  const target = targetTemp || 55;
  
  // Calculate color based on difference from target
  const getColor = () => {
    if (temperature === null) return 'bg-secondary';
    
    const diff = Math.abs(temperature - target);
    if (diff <= 2) return '#20c997'; // green
    if (diff <= 5) return '#ffc107'; // yellow
    return '#dc3545'; // red
  };
  
  // Calculate the position on the gauge (as percentage)
  const getPosition = () => {
    if (temperature === null) return 50; // Center when no data
    
    // Constrain within min-max
    const constrained = Math.max(min, Math.min(max, temperature));
    return ((constrained - min) / (max - min)) * 100;
  };
  
  // Calculate target position
  const getTargetPosition = () => {
    const constrained = Math.max(min, Math.min(max, target));
    return ((constrained - min) / (max - min)) * 100;
  };
  
  // Create gradient background with colored temperature zones
  const gradientBackground = `linear-gradient(to right, 
    #20c997 30%, /* Cool - green */
    #ffc107 60%, /* Warm - yellow */
    #dc3545 100% /* Very hot - red */
  )`;
  
  return (
    <div className="temperature-gauge mb-2">
      <div className="d-flex justify-content-between mb-1">
        <small>{min}°F</small>
        <small>{currentTemp !== '--' ? `${currentTemp}°F` : '--'}</small>
        <small>{max}°F</small>
      </div>
      
      {/* Gauge Track with gradient background */}
      <div 
        className="gauge-track position-relative" 
        style={{ 
          height: '14px', 
          background: gradientBackground, 
          borderRadius: '7px',
          overflow: 'hidden',
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
        }}
      >
        {/* Adding a semi-transparent overlay for better contrast */}
        <div 
          className="gauge-overlay" 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0)',
            zIndex: 0
          }}
        />
        
        {/* Target temperature marker */}
        <div 
          className="target-marker position-absolute bg-dark" 
          style={{ 
            left: `${getTargetPosition()}%`, 
            width: '3px', 
            height: '18px', 
            top: '-2px',
            transform: 'translateX(-50%)',
            zIndex: 2,
            boxShadow: '0 0 2px rgba(0,0,0,0.5)'
          }}
        />
        
        {/* Current temperature indicator */}
        {temperature !== null && (
          <div 
            className="temperature-indicator position-absolute bg-white" 
            style={{ 
              left: `${getPosition()}%`, 
              width: '16px', 
              height: '16px', 
              top: '-1px', 
              borderRadius: '50%',
              transform: 'translateX(-50%)',
              zIndex: 3,
              border: `3px solid ${getColor()}`,
              boxShadow: '0 0 4px rgba(0,0,0,0.4)'
            }}
          />
        )}
      </div>
    </div>
  );
};

export default TemperatureGauge;